import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { KeyRound, Shield, FileText, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "password", icon: KeyRound, label: "Set your password" },
  { key: "dbs",      icon: Shield,   label: "DBS certificate" },
  { key: "cert",     icon: FileText, label: "Partnership certificate" },
];

/**
 * First-login onboarding for a freshly invited cleaner. Only mounts when the
 * account is flagged `must_change_password: true`. Guides the cleaner through
 * (1) picking a permanent password, (2) reminder to upload DBS, (3) reminder
 * to sign the partnership certificate. Steps 2/3 point them at the existing
 * portal tabs — the wizard just makes sure they know they need to do them.
 */
export const FirstLoginWizard = ({ open, onOpenChange, onSwitchTab, onPasswordChanged }) => {
  const [step, setStep] = useState(0);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const totalSteps = STEPS.length;
  const progress = Math.round(((step + 1) / totalSteps) * 100);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/cleaner/change-password`, {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
      onPasswordChanged?.();
      setStep(1);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Password change failed");
    } finally {
      setBusy(false);
    }
  };

  const goToTab = (tabId) => {
    onSwitchTab?.(tabId);
    onOpenChange?.(false);
    setStep(0); // reset for next opening
  };

  const finish = () => {
    onOpenChange?.(false);
    setStep(0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="first-login-wizard">
        <DialogHeader>
          <DialogTitle>Welcome to Clean Kin</DialogTitle>
          <DialogDescription>
            Three quick steps to get your account fully live. This takes about two minutes.
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="space-y-2" data-testid="wizard-progress">
          <div className="flex items-center justify-between text-xs text-[#5C6B57]">
            <span>Step {step + 1} of {totalSteps}</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex items-center gap-3 pt-1">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center gap-1.5 text-[11px]">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  i < step ? "bg-[#7CB342] text-white"
                  : i === step ? "bg-[#2D5016] text-white"
                  : "bg-[#E2E8E0] text-[#5C6B57]"
                }`}>
                  {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                </div>
                <span className={i === step ? "text-[#1A2E12] font-semibold" : "text-[#5C6B57]"}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        {step === 0 && (
          <form onSubmit={submitPassword} className="space-y-3 pt-2" data-testid="wizard-step-password">
            <p className="text-sm text-[#5C6B57]">
              You&apos;re signed in with a temporary password. Please choose your own now.
            </p>
            <div>
              <Label className="text-xs text-[#5C6B57]">Temporary password</Label>
              <Input
                type="password"
                value={pw.current}
                onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
                required
                className="mt-1"
                autoComplete="current-password"
                data-testid="wizard-current-password"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6B57]">New password (min. 8 chars)</Label>
              <Input
                type="password"
                value={pw.next}
                onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
                required
                className="mt-1"
                autoComplete="new-password"
                data-testid="wizard-new-password"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6B57]">Confirm new password</Label>
              <Input
                type="password"
                value={pw.confirm}
                onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                required
                className="mt-1"
                autoComplete="new-password"
                data-testid="wizard-confirm-password"
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                className="bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                disabled={busy}
                data-testid="wizard-submit-password"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === 1 && (
          <div className="space-y-4 pt-2" data-testid="wizard-step-dbs">
            <div className="bg-[#F9FAF9] border border-[#E2E8E0] rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-[#2D5016] mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#1A2E12]">Upload your DBS certificate</h4>
                  <p className="text-sm text-[#5C6B57] mt-1">
                    Every Clean Kin cleaner is required to hold a current DBS check. Upload a PDF now so admin can verify you before your first shift.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-[#5C6B57]">
              Don&apos;t have it to hand? Skip for now — you can upload from the DBS tab any time.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                data-testid="wizard-skip-dbs"
              >
                Skip
              </Button>
              <Button
                onClick={() => goToTab("dbs")}
                className="bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                data-testid="wizard-goto-dbs"
              >
                Take me to DBS upload
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 pt-2" data-testid="wizard-step-cert">
            <div className="bg-[#F9FAF9] border border-[#E2E8E0] rounded-xl p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-6 h-6 text-[#2D5016] mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#1A2E12]">Sign the partnership certificate</h4>
                  <p className="text-sm text-[#5C6B57] mt-1">
                    Clean Kin cleaners work as self-employed partners. Sign the short declaration confirming you handle your own tax and are registered where required.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={finish}
                data-testid="wizard-finish-later"
              >
                Finish later
              </Button>
              <Button
                onClick={() => goToTab("certificate")}
                className="bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                data-testid="wizard-goto-cert"
              >
                Sign it now
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FirstLoginWizard;
